import 'react-tabs-scrollable/dist/rts.css';

import React, { BaseSyntheticEvent, RefObject, useEffect, useRef, useState } from 'react';
import { Tab, Tabs } from 'react-tabs-scrollable';

import { ArrowBlack } from '../Icons/ArrowBlack';
import { trackJourneyElement } from '@/utils/eventTracking';

interface ScrollableTabsProps {
	tabs: string[];
	activeTab: number;
	refs: RefObject<(HTMLDivElement | null)[]>;
	scrollToSection: (event: BaseSyntheticEvent, index: number) => void;
}

const ScrollableTabs: React.FC<ScrollableTabsProps> = ({ tabs, activeTab, scrollToSection }) => {
	const tabsContainerRef = useRef<HTMLDivElement | null>(null);
	const [isUserScrolling, setIsUserScrolling] = useState(false);
	const [manualTab, setManualTab] = useState(activeTab);
	const [initialRender, setInitialRender] = useState(true);

	useEffect(() => {
		const handleScroll = () => {
			setIsUserScrolling(true);
			clearTimeout(window.scrollTimeout as unknown as number);
			window.scrollTimeout = setTimeout(() => setIsUserScrolling(false), 300);
		};

		const tabContainer = tabsContainerRef.current;
		if (tabContainer) {
			tabContainer.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (tabContainer) {
				tabContainer.removeEventListener('scroll', handleScroll);
			}
		};
	}, []);

	// Track initial tab display
	useEffect(() => {
		// Track initial active tab
		trackJourneyElement({
			type: 'tab',
			action: 'view',
			location: `scrollable_tabs_${tabs[activeTab] || 'unknown'}`,
			pathname: window.location.pathname,
			domain: window.location.hostname,
		});

		setInitialRender(false);
	}, [activeTab, tabs]);

	useEffect(() => {
		if (!isUserScrolling) {
			setManualTab(activeTab); // Update only when user isn't scrolling

			// Track tab changes from scroll (not manual tab clicking)
			// Only track if not the initial render and if actually changing tabs
			if (!initialRender && manualTab !== activeTab) {
				trackJourneyElement({
					type: 'tab',
					action: 'view_from_scroll',
					location: `scrollable_tabs_${tabs[activeTab] || 'unknown'}`,
					pathname: window.location.pathname,
					domain: window.location.hostname,
				});
			}
		}
	}, [activeTab, isUserScrolling, tabs, manualTab, initialRender]);

	const handleTabClick = (event: BaseSyntheticEvent, index: number) => {
		setManualTab(index);

		// Track tab click
		trackJourneyElement({
			type: 'tab',
			action: 'click',
			location: `scrollable_tabs_${tabs[index] || 'unknown'}`,
			pathname: window.location.pathname,
			domain: window.location.hostname,
		});

		scrollToSection(event, index);
	};

	return (
		<div
			className="tab-container container me-auto ms-auto flex items-center justify-start"
			ref={tabsContainerRef}
		>
			<Tabs
				mode="scrollSelectedToCenterFromView"
				activeTab={manualTab}
				onTabClick={handleTabClick}
				rightBtnIcon={
					<ArrowBlack
						className="icon ml-auto self-center stroke-none"
						style={{
							background: 'linear-gradient(90deg, transparent, 10%, #fff)',
							width: '40px',
							height: '15px',
						}}
					/>
				}
				leftBtnIcon={
					<ArrowBlack
						className="icon ml-auto rotate-180 self-center stroke-none"
						style={{
							background: 'linear-gradient(90deg, transparent, 10%, #fff)',
							width: '40px',
							height: '15px',
						}}
					/>
				}
				hideNavBtns={false}
				hideNavBtnsOnMobile={false}
				leftNavBtnClassName="left-0"
				rightNavBtnClassName="right-0"
				navBtnStyle={{
					'--rts-primary-color': '#fff',
					borderStyle: 'none',
					height: '20px',
					position: 'absolute',
					top: '9px',
					zIndex: '200',
				}}
			>
				{tabs.map((tab, index) => (
					<Tab
						key={index}
						className={`tab-list ${manualTab === index ? 'active-tab' : ''} menu_category_tab_cta`}
					>
						{tab}
					</Tab>
				))}
			</Tabs>
		</div>
	);
};

export default ScrollableTabs;
